import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { Key } from "ts-key-enum";

import styles from "../GridFilter/index.module.scss";

/**
 * One collapsible section of the grid's sidebar column.
 *
 * The shell every section shares: the fieldset, the heading that toggles it,
 * the ARIA wiring between the two, and the global class hooks sites style
 * against. `GridFilter` and `GridSort` supply a title and a body and say
 * whether they are open; how a section opens is decided once, here.
 *
 * Owning the behaviour matters more than owning the markup. Two hand-written
 * copies of a toggle look identical until one of them is fixed -- a keyboard
 * activation key, a heading level, an ARIA attribute -- and the other is not.
 *
 * Open state stays with the caller: `GridFilter` keeps it in redux because
 * two components render the same filter and have to agree, `GridSort` keeps
 * it locally because nothing else renders it. That difference is real, so it
 * is a prop rather than something this decides.
 *
 * Still borrowing `GridFilter`'s stylesheet rather than holding its own:
 * moving those rules here would rename their generated classes, which the
 * snapshots across every site are pinned to. Worth doing, separately.
 */
type IProps = Immutable<{
    /**
     * The body element's id, which the heading points at.
     *
     * Given rather than derived so a section keeps whatever id it already
     * published -- the filters' bodies were `filter-body-<id>` before this
     * shell existed, and a rename would be a change to every site's DOM for
     * no one's benefit.
     */
    bodyID: string;
    isOpen: boolean;
    /** Extra global hooks, so a section can be styled as its own kind. */
    headerClassName?: string;
    bodyClassName?: string;
}> & {
    // Outside the `Immutable<>` wrapper, as the other grid components put
    // theirs: a deep readonly over `ReactNode` rewrites a type React owns,
    // and a callback has nothing to freeze.
    title: React.ReactNode;
    children: React.ReactNode;
    onToggle: () => void;
};

export const GridAccordion = (props: IProps): React.JSX.Element => {
    const titleClasses = classNames({
        [styles.filterTitle]: true,
        [styles.filterTitleOpen]: props.isOpen,
        [styles.filterTitleClose]: !props.isOpen,
        "product-grid-filter__header": true,
        [props.headerClassName ?? ""]: !!props.headerClassName,
    });
    const bodyClasses = classNames({
        [styles.filterBody]: true,
        "product-grid-filter__body--open": props.isOpen,
        [styles.filterBodyClose]: !props.isOpen,
        [props.bodyClassName ?? ""]: !!props.bodyClassName,
    });

    return (
        <fieldset className={styles.filterContainer}>
            <legend
                className={titleClasses}
                role="button"
                tabIndex={0}
                aria-controls={props.bodyID}
                aria-expanded={props.isOpen}
                onClick={(event) => {
                    event.preventDefault();
                    props.onToggle();
                }}
                // `keydown`, and Space as well as Enter: an element given
                // `role="button"` is expected to answer both, and `keypress`
                // is gone from the DOM spec. Space is prevented from doing
                // what it would otherwise do here, which is scroll the page.
                onKeyDown={(event) => {
                    if (
                        event.key === Key.Enter ||
                        event.key === " " ||
                        event.key === "Spacebar"
                    ) {
                        event.preventDefault();
                        props.onToggle();
                    }
                }}
            >
                {props.title}
            </legend>

            <div className={bodyClasses} id={props.bodyID}>
                {props.children}
            </div>
        </fieldset>
    );
};
